import { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const severityBadgeClass = (severity) => {
  if (severity === 'high') return 'bg-red-100 text-red-700';
  if (severity === 'medium') return 'bg-amber-100 text-amber-800';
  return 'bg-slate-100 text-slate-700';
};

const statusBadgeClass = (status) => {
  if (status === 'resolved') return 'bg-emerald-100 text-emerald-800';
  if (status === 'acknowledged') return 'bg-blue-100 text-blue-800';
  return 'bg-[#F5EFE9] text-[#5C4033]';
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN');
};

export default function ReportsAlertsPage() {
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [hubData, setHubData] = useState(null);

  const [status, setStatus] = useState('open');
  const [severity, setSeverity] = useState('all');
  const [source, setSource] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchData = async (overrides = {}) => {
    setLoading(true);
    try {
      const params = {
        status,
        limit: 100,
        ...(severity !== 'all' ? { severity } : {}),
        ...(source !== 'all' ? { source } : {}),
        ...(dateFrom ? { date_from: dateFrom } : {}),
        ...(dateTo ? { date_to: dateTo } : {}),
        ...overrides,
      };
      const response = await adminApi.getAlertingHub(params);
      setHubData(response.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to fetch alerting hub');
      setHubData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData({ status: 'open' });
  }, []);

  const applyFilters = async () => {
    await fetchData();
  };

  const resetFilters = async () => {
    setStatus('open');
    setSeverity('all');
    setSource('all');
    setDateFrom('');
    setDateTo('');
    await fetchData({
      status: 'open',
      limit: 100,
      severity: undefined,
      source: undefined,
      date_from: undefined,
      date_to: undefined,
    });
  };

  const updateAlert = async (alertKey, nextStatus) => {
    setActing(true);
    try {
      await adminApi.updateAlertingStatus({ alert_key: alertKey, status: nextStatus });
      toast.success(`Alert marked as ${nextStatus}`);
      await fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update alert status');
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-alerts-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Alerting Hub</h1>
        <p className="text-[#A8A8A8] mt-1">Failed jobs, backup anomalies, and payment aging spikes</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader className="border-b border-[#E5E5E5]">
          <CardTitle className="text-lg text-[#2D2420]">Filters</CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>

            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger><SelectValue placeholder="Severity" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>

            <Select value={source} onValueChange={setSource}>
              <SelectTrigger><SelectValue placeholder="Source" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="backup">Backup</SelectItem>
                <SelectItem value="reminder">Reminder</SelectItem>
                <SelectItem value="payments">Payments</SelectItem>
              </SelectContent>
            </Select>

            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>

          <div className="flex gap-2">
            <Button onClick={applyFilters} className="bg-[#C97A5E] hover:bg-[#B06850]" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Apply
            </Button>
            <Button variant="outline" onClick={resetFilters} disabled={loading}>Reset</Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-xs text-[#A8A8A8]">Total</p><p className="text-2xl font-bold text-[#2D2420]">{hubData?.summary?.total || 0}</p></CardContent></Card>
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-xs text-red-600">High</p><p className="text-2xl font-bold text-red-700">{hubData?.summary?.by_severity?.high || 0}</p></CardContent></Card>
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-xs text-amber-700">Medium</p><p className="text-2xl font-bold text-amber-800">{hubData?.summary?.by_severity?.medium || 0}</p></CardContent></Card>
        <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-xs text-[#5C4033]">Open</p><p className="text-2xl font-bold text-[#2D2420]">{hubData?.summary?.by_status?.open || 0}</p></CardContent></Card>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader className="border-b border-[#E5E5E5]">
          <CardTitle className="text-lg text-[#2D2420]">Alerts</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 flex items-center justify-center gap-2 text-[#A8A8A8]">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading alerts...
            </div>
          ) : (
            <div className="divide-y divide-[#E5E5E5]">
              {(hubData?.items || []).map((alert) => (
                <div key={alert.alert_key} className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                    <div>
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Badge className={severityBadgeClass(alert.severity)}>{alert.severity}</Badge>
                        <Badge className="bg-blue-100 text-blue-800">{alert.source}</Badge>
                        <Badge className={statusBadgeClass(alert.status)}>{alert.status}</Badge>
                      </div>
                      <p className="font-semibold text-[#2D2420]">{alert.title}</p>
                      <p className="text-sm text-[#5C4033] mt-1">{alert.message}</p>
                      <p className="text-xs text-[#A8A8A8] mt-1">{formatDateTime(alert.created_at)}</p>
                    </div>

                    <div className="flex gap-2">
                      {alert.status !== 'acknowledged' && alert.status !== 'resolved' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={acting}
                          onClick={() => updateAlert(alert.alert_key, 'acknowledged')}
                        >
                          Acknowledge
                        </Button>
                      ) : null}
                      {alert.status !== 'resolved' ? (
                        <Button
                          size="sm"
                          className="bg-[#2A9D8F] hover:bg-[#24897d]"
                          disabled={acting}
                          onClick={() => updateAlert(alert.alert_key, 'resolved')}
                        >
                          Resolve
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}

              {!(hubData?.items || []).length && (
                <p className="p-6 text-center text-[#A8A8A8]">No alerts found for selected filters.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
